import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthService } from '@services/auth.service';
import { environment } from 'src/environments/environment';
import { first } from 'rxjs/operators';

export type NovaIngestStatus =
    | 'queued'
    | 'fetching'
    | 'copying_assets'
    | 'transcribing'
    | 'snapshot_captions'
    | 'indexing'
    | 'case'
    | 'ready'
    | 'error'
    | 'deleted';

export const NOVA_TERMINAL_STATUSES: Set<string> = new Set(['ready', 'error', 'deleted']);

export interface NovaIngestJobResult {
    totalChunks?: number;
    languages?: string[];
    hasCase?: boolean;
    hasCaptions?: boolean;
    evidenceCopied?: {
        transcript?: number;
        snapshots?: number;
        recordings?: number;
    };
}

export interface NovaIngestJob {
    jobId: string;
    status: NovaIngestStatus;
    attemptId?: string;
    attemptNumber?: number;
    sessionId: string;
    roomId?: string;
    roomName?: string;
    statusMessage?: string;
    createdAt?: number;
    updatedAt?: number;
    startedAt?: number;
    completedAt?: number;
    errorMessage?: string;
    result?: NovaIngestJobResult;
    isNew?: boolean;
    message?: string;
}

@Injectable({
    providedIn: 'root'
})
export class NovaIngestService {

    private get baseUrl(): string {
        const projectId = environment.firebase.projectId;
        return `https://us-central1-${projectId}.cloudfunctions.net/novaIngest/remote-session`;
    }

    constructor(
        private http: HttpClient,
        private authService: AuthService
    ) { }

    private getAuthHeaders(): HttpHeaders {
        const token = this.authService.currentUser?.token;
        return new HttpHeaders({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        });
    }

    triggerIngest(accountId: string, roomId: string, sessionId: string, force: boolean = false): Promise<NovaIngestJob> {
        const body: any = { accountId, roomId, sessionId };
        if (force) {
            body.force = true;
        }
        return this.http.post<{ success: boolean; data: NovaIngestJob }>(
            this.baseUrl,
            body,
            { headers: this.getAuthHeaders() }
        ).pipe(first()).toPromise().then(res => res.data);
    }

    getJobStatus(sessionId: string): Promise<NovaIngestJob> {
        return this.http.get<{ success: boolean; data: NovaIngestJob }>(
            `${this.baseUrl}/${sessionId}`,
            { headers: this.getAuthHeaders() }
        ).pipe(first()).toPromise().then(res => res.data);
    }

    deleteFromNova(sessionId: string, roomId: string): Promise<any> {
        return this.http.delete<{ success: boolean; data: any }>(
            `${this.baseUrl}/${sessionId}?roomId=${encodeURIComponent(roomId)}`,
            { headers: this.getAuthHeaders() }
        ).pipe(first()).toPromise().then(res => res.data);
    }
}
